import { useStatsigClient } from '@statsig/react-bindings'
import {
  BlocksRenderer,
  type BlocksContent,
} from '@strapi/blocks-react-renderer'
import { type ReactQuery } from 'cms-api'
import { rem } from 'polished'
import React from 'react'
import { Box, Container, Flex } from 'components/Layout'
import 'swiper/css/bundle'
import { StyledLink } from 'components/Link'
import { Text } from 'components/Typography'
import { SCHEMA_PROPS, SCHEMA_TYPES } from 'constants/common'
import { STATSIG_EVENTS } from 'providers/statsig'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, FONT_SIZE, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { StrapiFeaturedExpertsWidgetCarousel } from './StrapiFeaturedExpertsWidgetCarousel'

const MAX_CAROUSEL_SLIDES = {
  MOBILE: 2,
  TABLET: 4,
  DESKTOP: 6,
}

export const StrapiFeaturedExpertsWidget: React.FC<
  React.PWC<ReactQuery.WidgetsFeaturedExpertsComponent>
> = ({ title, subtitle, experts, buttonText, buttonLink }) => {
  const { client: statsigClient } = useStatsigClient()
  const { user } = useUserAuth()
  return (
    <section itemScope itemType={SCHEMA_TYPES.THING}>
      <meta
        itemProp={SCHEMA_PROPS.ALTERNATE_NAME}
        content="Featured experts widget"
      />
      <Box backgroundColor="#EBF2F1" py={{ MOBILE: rem(40), TABLET: rem(54) }}>
        <Container mb={SPACE.PX_16}>
          <BlocksRenderer
            content={title as BlocksContent}
            blocks={{
              paragraph: ({ children }) => (
                <Text
                  as="h1"
                  fontSize={{
                    MOBILE: FONT_SIZE.PX_24,
                    TABLET: rem(40),
                  }}
                  color={COLOR_INTENT.GRAY_90}
                  fontWeight={FONT_WEIGHT.NORMAL}
                  my={SPACE.PX_12}
                  textAlign="center"
                  fontFamily={FONT_STACK.SERIF}
                  lineHeight="140%"
                >
                  {children}
                </Text>
              ),
            }}
          />
          <Flex justifyContent="center" mt={SPACE.PX_24}>
            <Text
              style={{ maxWidth: rem(320) }}
              textAlign="center"
              fontSize={FONT_SIZE.PX_16}
            >
              {subtitle}
            </Text>
          </Flex>
        </Container>
        {/* Mobile View */}
        <Box
          display={{ MOBILE: 'block', TABLET: 'none', DESKTOP: 'none' }}
          mt={rem(44)}
        >
          <StrapiFeaturedExpertsWidgetCarousel
            experts={experts}
            maxSlidesCount={MAX_CAROUSEL_SLIDES.MOBILE}
          />
        </Box>
        {/* Tablet View */}
        <Box
          display={{ MOBILE: 'none', TABLET: 'block', DESKTOP: 'none' }}
          mt={rem(44)}
        >
          <StrapiFeaturedExpertsWidgetCarousel
            experts={experts}
            maxSlidesCount={MAX_CAROUSEL_SLIDES.TABLET}
          />
        </Box>
        {/* Desktop View */}
        <Box display={{ MOBILE: 'none', DESKTOP: 'block' }} mt={rem(44)}>
          <StrapiFeaturedExpertsWidgetCarousel
            experts={experts}
            maxSlidesCount={MAX_CAROUSEL_SLIDES.DESKTOP}
          />
        </Box>
        {buttonLink && buttonText && (
          <Flex width="100%" justifyContent="center" mt={SPACE.PX_40}>
            <StyledLink
              onClick={() => {
                if (statsigClient) {
                  statsigClient.logEvent(
                    STATSIG_EVENTS.STRAPI_FEATURED_EXPERTS_WIDGET_ACTION,
                    buttonText,
                    {
                      isTradeCapable: user?.isTradeCapable ? 'true' : 'false',
                      target: buttonLink,
                    }
                  )
                }
              }}
              href={buttonLink}
              aria-label={buttonText}
            >
              {buttonText}
            </StyledLink>
          </Flex>
        )}
      </Box>
    </section>
  )
}
